import { uploadCommentFile } from '@/components/services/uploadCommentFile';
import { CommentsV2CacheItem } from '@/db/db';
import {
  addCommentToCacheV2,
  getAllCommentsByDdocIdV2,
  updateCommentIndexV2,
  updateDeleteOnCommentV2,
  updateRepliesOnCommentV2,
  updateReplyCommentIndexV2,
  updateResolveOnCommentV2,
  getCommentItemById,
  updateCommentContentHashV2,
  updateTimestampOnCommentV2,
} from '@/db/queries';
import {
  addCommentTransaction,
  addReplyTransaction,
  checkIfCommentTxSuccessful,
  checkIfReplyTxSuccessful,
  CommentsArray,
  deleteCommentTransaction,
  getAllComments,
  getAllReplies,
  getCommentCount,
  getCommentsDataById,
  getReplyCount,
  OnchainComment,
  removeDuplicatesRepliesByLatest,
  Reply,
  resolveCommentTransaction,
  unresolveCommentTransaction,
} from '@/utils/comment-utils';
import { decryptData, encryptData } from '@/utils/crypto-utils';
import { getIPFSAsset } from '@/utils/file-utils';
import { IComment } from '@fileverse-dev/ddoc/types';
import { useLiveQuery } from 'dexie-react-hooks';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import useCommentAccount from './use-comment-account';
import { useAccountName } from '@/components/providers/account-name-provider';
import { captureException } from '@sentry/nextjs';
import { LATEST_DDOC_VERSION } from '@/utils/constants';
import { useMigrateComments } from './use-migrate-comments';
import { extractNonceFromMessage } from '@/utils/extractNonceFromMessage';
import { toast } from '@fileverse/ui';
import { uuid } from 'short-uuid';
import { Hex } from 'viem';

export interface NewIComment extends IComment {
  commentIndex: number;
}

interface ReplyQueue {
  reply: IComment;
  commentId: string;
  commentIndex: number;
}

type Queue = {
  id: string;
  job: 'resolve' | 'delete' | 'unresolve';
  data: any;
};

const useCommentV2 = ({
  ddocId,
  portalAddress,
  commentKey,
  setCollaboratorName,
  editorRef,
  version,
}: {
  ddocId: string;
  portalAddress: string;
  editorRef: MutableRefObject<any>;
  commentKey: string;
  setCollaboratorName: (n: string) => void;
  version: string | null;
}) => {
  const [commentQueue, setCommentQueue] = useState<CommentsV2CacheItem[]>([]);
  const [isProcessingComment, setIsProcessingComment] =
    useState<boolean>(false);
  const [replyQueue, setReplyQueue] = useState<ReplyQueue[]>([]);
  const [isProcessingReply, setIsProcessingReply] = useState<boolean>(false);
  const shouldPauseProcessingRef = useRef<boolean>(false);
  const [operationQueue, setOperationQueue] = useState<Queue[]>([]);
  const [isProcessingOperation, setProcessingOperationQueue] =
    useState<boolean>(false);

  const pauseProcessing = (shouldPause: boolean) => {
    shouldPauseProcessingRef.current = shouldPause;
  };
  const [updatingComments, setUpdatingComments] = useState<boolean>(false);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (
        commentQueue.length > 0 ||
        replyQueue.length > 0 ||
        operationQueue.length > 0 ||
        isProcessingReply ||
        isProcessingOperation
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [commentQueue, replyQueue, operationQueue]);

  const encryptAndUploadComment = async (comment: CommentsV2CacheItem) => {
    try {
      if (!comment.id || !comment.username) {
        throw new Error(
          'Cannot upload comment file with missing comment id or username'
        );
      }
      const commentData = {
        selectedText: comment.selectedContent || '',
        highlightYjsDiff: comment.highlightYjsDiff,
        content: comment.content,
      };
      const { encryptedData } = await encryptData(
        JSON.stringify(commentData),
        commentKey
      );
      const _comment = {
        id: comment.id,
        username: comment.username,
        encryptedData,
        timestamp: comment.createdAt?.getTime(),
      };
      const commentJson = JSON.stringify(_comment);
      const commentFile = new File([commentJson], 'data.json', {
        type: 'application/json',
      });
      const { data } = await uploadCommentFile(commentFile);
      await handleAddCommentTransaction(comment.id as string, data.ipfsHash);
    } catch (error) {
      console.log(error);
      captureException(error || 'failed to add comment');
      toast({
        variant: 'secondary',
        description: "Couldn't sync comment, retrying again!",
      });
    }
  };

  const handleAddCommentTransaction = async (
    commentId: string,
    contentHash: string
  ) => {
    if (!smartAccountClient || !portalAddress) return;
    try {
      pauseProcessing(true);
      const res = await addCommentTransaction(
        smartAccountClient!,
        {
          portal: portalAddress as string,
          ddocId: ddocId,
          contentHash,
        },
        commentId
      );
      await updateCommentIndexV2(ddocId, commentId, Number(res.commentIndex));
      await updateCommentContentHashV2(ddocId, commentId, res.contentHash);
    } catch (error) {
      console.log(error);
      captureException(error || 'failed to publish comment');
      toast({
        variant: 'danger',
        description: 'Failed to publish comment',
      });
    } finally {
      pauseProcessing(false);
    }
  };

  const encryptAndUploadReply = async (
    reply: IComment,
    commentId: string,
    commentIndex: number
  ) => {
    const replyData = {
      selectedText: reply.selectedContent || '',
      highlightYjsDiff: '',
      content: reply.content,
    };
    const { encryptedData } = await encryptData(
      JSON.stringify(replyData),
      commentKey
    );
    const _reply = {
      id: reply.id,
      username: reply.username,
      encryptedData,
      timestamp: reply.createdAt?.getTime(),
    };
    const replyJson = JSON.stringify(_reply);
    const replyFile = new File([replyJson], 'data.json', {
      type: 'application/json',
    });
    const { data } = await uploadCommentFile(replyFile);
    await handleAddReplyTransaction(
      commentId,
      reply.id!,
      commentIndex,
      data.ipfsHash
    );
  };

  const handleAddReplyTransaction = async (
    commentId: string,
    replyId: string,
    commentIndex: number,
    contentHash: string
  ) => {
    if (!smartAccountClient || !portalAddress) return;

    try {
      pauseProcessing(true);
      const replyIndex = await addReplyTransaction(
        smartAccountClient!,
        {
          portal: portalAddress as string,
          ddocId: ddocId,
          commentIndex,
          contentHash,
        },
        commentId,
        replyId
      );
      await updateReplyCommentIndexV2(
        ddocId,
        commentId,
        replyId,
        Number(replyIndex)
      );
    } catch (error) {
      console.log(error);
      captureException(error || 'failed to publish reply');
      toast({
        variant: 'danger',
        description: 'Failed to publish reply',
      });
    } finally {
      pauseProcessing(false);
    }
  };

  const handleResolveCommentTransaction = async (
    commentIndex: number,
    contentHash: string,
    update: string | null,
    commentId: string
  ) => {
    if (!smartAccountClient || !portalAddress) return;
    pauseProcessing(true); // pause so that indexer doesn't bring back resolved comments due to it publishing not completed
    try {
      let hash = contentHash;
      if (update) {
        hash = await getNewContentHash({ resolvedUpdate: update }, contentHash);
      }
      const result = await resolveCommentTransaction(smartAccountClient!, {
        portal: portalAddress as string,
        ddocId: ddocId,
        commentIndex,
        contentHash: hash,
      });
      await updateCommentContentHashV2(ddocId, commentId, result.contentHash);
    } catch (error: any) {
      console.log(error);
      captureException(error || 'Failed to resolve comment');
      toast({
        variant: 'danger',
        description: error?.message || 'Failed to resolve comment',
      });
    } finally {
      pauseProcessing(false);
    }
  };

  const handleUnresolveCommentTransaction = async (
    commentIndex: number,
    contentHash: string,
    update: string | null,
    commentId: string
  ) => {
    try {
      pauseProcessing(true);
      let hash = contentHash;
      if (update) {
        hash = await getNewContentHash(
          { unresolvedUpdate: update },
          contentHash
        );
      }
      const result = await unresolveCommentTransaction(smartAccountClient!, {
        portal: portalAddress as string,
        ddocId: ddocId,
        commentIndex,
        contentHash: hash,
      });

      await updateCommentContentHashV2(ddocId, commentId, result.contentHash);
    } catch (error: any) {
      console.log(error);
      captureException(error || 'Failed to unresolve comment');
      toast({
        variant: 'danger',
        description: error?.message || 'Failed to unresolve comment',
      });
    } finally {
      pauseProcessing(false);
    }
  };

  const getNewContentHash = async (
    contentData: Record<string, any>,
    hash: string
  ) => {
    const { data: metadata } = await getIPFSAsset({
      ipfsHash: hash,
    });
    const { nonce, encryptedMessage } = extractNonceFromMessage(
      ddocId,
      metadata.encryptedData
    );
    const decryptedChattext = await decryptData(
      encryptedMessage,
      commentKey,
      nonce
    );

    const decryptedComment = JSON.parse(decryptedChattext);
    let newHashData = { ...decryptedComment };

    if (contentData.resolvedUpdate) {
      let dataToInsert = [];
      const previousResolvedData = decryptedComment.resolvedUpdate;

      if (previousResolvedData) {
        dataToInsert = JSON.parse(previousResolvedData);
      }
      dataToInsert = [
        ...dataToInsert,
        ...JSON.parse(contentData.resolvedUpdate),
      ];

      newHashData.resolvedUpdate = JSON.stringify(dataToInsert);
    }

    if (contentData.deletedUpdate) {
      let dataToInsert = [];
      const previousDeletedData = decryptedComment.deletedUpdate;

      if (previousDeletedData) {
        dataToInsert = JSON.parse(previousDeletedData);
      }
      dataToInsert = [
        ...dataToInsert,
        ...JSON.parse(contentData.deletedUpdate),
      ];

      newHashData.deletedUpdate = JSON.stringify(dataToInsert);
    }

    if (contentData.unresolvedUpdate) {
      let dataToInsert = [];
      const previousDeletedData = decryptedComment.unresolvedUpdate;

      if (previousDeletedData) {
        dataToInsert = JSON.parse(previousDeletedData);
      }
      dataToInsert = [
        ...dataToInsert,
        ...JSON.parse(contentData.unresolvedUpdate),
      ];
      newHashData.unresolvedUpdate = JSON.stringify(dataToInsert);
    }

    const { encryptedData } = await encryptData(
      JSON.stringify(newHashData),
      commentKey
    );
    const newMetadata = { ...metadata, encryptedData };
    const commentJson = JSON.stringify(newMetadata);
    const commentFile = new File([commentJson], 'data.json', {
      type: 'application/json',
    });
    const { data } = await uploadCommentFile(commentFile);
    return data.ipfsHash;
  };

  const handleDeleteCommentTransaction = async (
    commentIndex: number,
    contentHash: string,
    update: string | null,
    commentId: string
  ) => {
    pauseProcessing(true);
    if (!smartAccountClient || !portalAddress) return;
    try {
      let hash = contentHash;
      if (update) {
        hash = await getNewContentHash({ deletedUpdate: update }, contentHash);
      }
      const result = await deleteCommentTransaction(smartAccountClient!, {
        portal: portalAddress as string,
        ddocId: ddocId,
        commentIndex,
        contentHash: hash,
      });
      await updateCommentContentHashV2(ddocId, commentId, result.contentHash);
    } catch (error: any) {
      captureException(error || 'Failed to delete comment');
      toast({
        variant: 'danger',
        description: error?.message || 'Failed to unresolve comment',
      });
    } finally {
      pauseProcessing(false);
    }
  };
  const { setAccountName } = useAccountName();
  const {
    isConnected,
    isLoading,
    createRandomAccount,
    connectViaWallet,
    smartAccountClient,
  } = useCommentAccount((n) => {
    setCollaboratorName(n);
    setAccountName(n);
  });

  useEffect(() => {
    if (
      !commentQueue.length ||
      !smartAccountClient ||
      !portalAddress ||
      isProcessingComment
    )
      return;
    const commentsToProcess = commentQueue.slice(0, 5);
    setIsProcessingComment(true);
    Promise.all(
      commentsToProcess.map((comment) => encryptAndUploadComment(comment))
    )
      .then(() => {
        setCommentQueue((prev) =>
          prev.filter(
            (comment) => !commentsToProcess.some((c) => c.id === comment.id)
          )
        );
      })
      .finally(() => {
        setIsProcessingComment(false);
      });
  }, [commentQueue, smartAccountClient, portalAddress, isProcessingComment]);

  useEffect(() => {
    if (
      !replyQueue.length ||
      isProcessingReply ||
      !smartAccountClient ||
      !portalAddress
    )
      return;

    const repliesToProcess = replyQueue.slice(0, 5);
    setIsProcessingReply(true);

    Promise.all(
      repliesToProcess.map((reply) =>
        encryptAndUploadReply(reply.reply, reply.commentId, reply.commentIndex)
      )
    )
      .then(() => {
        setReplyQueue((prev) =>
          prev.filter(
            (comment) =>
              !repliesToProcess.some((r) => r.reply.id === comment.reply.id)
          )
        );
      })
      .finally(() => {
        setIsProcessingReply(false);
      });
  }, [replyQueue, isProcessingReply, smartAccountClient, portalAddress]);

  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);

  const highlightContentRef = useRef<string[]>([]);

  const userMessages = useLiveQuery(
    async () => {
      if (!ddocId) return [];
      const cachedComments = await getAllCommentsByDdocIdV2(ddocId);
      return cachedComments;
    },
    [ddocId],
    [] as CommentsV2CacheItem[]
  );
  const applyContent = (content: string[]) => {
    if (!content || content.length === 0) return;
    if (!editorRef?.current) {
      return;
    }
    editorRef.current.mergeYjsContents(content);
  };

  const handleReplyOnComment = async (id: string, reply: IComment) => {
    const _reply = { ...reply, txHash: '' };
    const thisComment = userMessages.filter((comment) => comment.id === id);
    const updatedReply = [...thisComment[0]?.replies!, _reply];

    await updateRepliesOnCommentV2(ddocId, id, updatedReply);
    setReplyQueue((prev) => [
      ...prev,
      { reply, commentId: id, commentIndex: thisComment[0].commentIndex! },
    ]);
  };
  const resetHighlight = () => {
    highlightContentRef.current.splice(0, highlightContentRef.current.length);
  };
  const handleNewComment = async (comment: IComment) => {
    const update = JSON.stringify(highlightContentRef.current);
    let highlight = '';
    if (comment.selectedContent) {
      highlight = update;
      resetHighlight();
    }

    const _comment: CommentsV2CacheItem = {
      ddocId,
      id: comment.id!,
      username: comment.username!,
      content: comment.content!,
      selectedContent: comment.selectedContent,
      highlightYjsDiff: highlight,
      createdAt: comment.createdAt,
      lastReadTimestamp: comment.createdAt?.getTime()!,
      reactions: [],
      replies: [],
      resolved: false,
      deleted: false,
      version: '2',
      contentHash: '',
      txHash: '',
    };
    await addCommentToCacheV2(_comment);
    setCommentQueue((prev) => [...prev, _comment]);
  };

  const fetchReplies = async (id: string, commentIndex: number) => {
    try {
      const response = await getAllReplies({
        portal: portalAddress as string,
        ddocId,
        commentIndex,
      });
      const replies: CommentsV2CacheItem['replies'] = [];
      for (let index = 0; index < response.length; index++) {
        const reply = response[index];

        const { data } = await getIPFSAsset({ ipfsHash: reply.contentHash });

        const { nonce, encryptedMessage } = extractNonceFromMessage(
          ddocId,
          data.encryptedData
        );
        const decryptedChattext = await decryptData(
          encryptedMessage,
          commentKey,
          nonce
        );
        const decryptedReply = JSON.parse(decryptedChattext);
        replies.push({
          id: data.id,
          content: decryptedReply.content,
          username: data.username,
          createdAt: new Date(Number(reply.timestamp) * 1000),
          commentIndex: index,
          txHash: '',
        });
      }
      return replies;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const getUpdate = () => {
    return JSON.stringify(
      highlightContentRef.current.splice(0, highlightContentRef.current.length)
    );
  };

  const pushToOperationQueue = (job: Omit<Queue, 'id'>) => {
    const _job: any = { ...job };
    _job.id = uuid();
    setOperationQueue((prev) => {
      const newQueue = [...prev, _job];
      return newQueue;
    });
  };

  const handleDeleteComment = async (id: string) => {
    await updateDeleteOnCommentV2(ddocId, id, true);
    const thisComment = userMessages.filter((comment) => comment.id === id);
    const commentIndex = thisComment[0].commentIndex!;
    const _comment = thisComment[0];
    const update = _comment.selectedContent ? getUpdate() : null;
    pushToOperationQueue({
      job: 'delete',
      data: {
        commentIndex,
        contentHash: _comment.contentHash,
        update,
        commentId: id,
      },
    });
  };

  const handleResolveComment = async (id: string) => {
    await updateResolveOnCommentV2(ddocId, id, true);
    const thisComment = userMessages.filter((comment) => comment.id === id);
    const commentIndex = thisComment[0].commentIndex!;
    const _comment = thisComment[0];
    const update = _comment.selectedContent ? getUpdate() : null;
    pushToOperationQueue({
      job: 'resolve',
      data: {
        commentIndex,
        contentHash: _comment.contentHash,
        update,
        commentId: id,
      },
    });
  };

  const handleUnresolveComment = async (id: string) => {
    await updateResolveOnCommentV2(ddocId, id, false);
    const thisComment = userMessages.filter((comment) => comment.id === id);
    const commentIndex = thisComment[0].commentIndex!;
    const _comment = thisComment[0];
    const update = _comment.selectedContent ? getUpdate() : null;
    pushToOperationQueue({
      job: 'unresolve',
      data: {
        commentIndex,
        contentHash: _comment.contentHash,
        update,
        commentId: id,
      },
    });
  };

  const handleHiglightUpdate = (_update: string) => {
    highlightContentRef.current = [...highlightContentRef.current, _update];
  };

  const handleSingleComment = async (
    comment: OnchainComment,
    index: number
  ) => {
    try {
      const { data } = await getIPFSAsset({
        ipfsHash: comment.contentHash,
      });
      const { nonce, encryptedMessage } = extractNonceFromMessage(
        ddocId,
        data.encryptedData
      );
      const decryptedChattext = await decryptData(
        encryptedMessage,
        commentKey,
        nonce
      );

      const decryptedComment = JSON.parse(decryptedChattext);
      if (decryptedComment.highlightYjsDiff && !comment.isDeleted) {
        applyContent(getYjsUpdate(decryptedComment.highlightYjsDiff));
      }
      const replies = await fetchReplies(data.id!, index);
      const updatedComment = {
        id: data.id,
        username: data.username,
        selectedContent: decryptedComment.selectedText || '',
        content: decryptedComment.content,
        commentMetadata: decryptedComment,
        resolved: comment.isResolved,
        deleted: comment.isDeleted,
        commentIndex: index,
        replies,
      };
      await addCommentToCacheV2({
        ddocId,
        id: data.id!,
        username: data.username!,
        highlightYjsDiff: decryptedComment.highlightYjsDiff,
        content: decryptedComment.content!,
        selectedContent: decryptedComment.selectedText,
        createdAt: new Date(Number(comment.timestamp) * 1000),
        reactions: decryptedComment.reactions || [],
        resolved: comment.isResolved,
        deleted: comment.isDeleted,
        commentIndex: index,
        contentHash: comment.contentHash,
        replies: replies,
        lastReadTimestamp: null,
        version: '2',
        txHash: '',
      });
      return updatedComment as IComment;
    } catch (error) {
      captureException(error);
    }
  };

  const fetchAndUpdateOnchainComments = async () => {
    const response = await getAllComments({
      portal: portalAddress as string,
      ddocId,
      resultsPerPage: 100,
      page: 1,
    });

    for (let index = 0; index < response.length; index++) {
      const comment = response[index];
      handleSingleComment(comment, index);
    }
    setUpdatingComments(false);
  };

  const handleIncomingComments = async () => {
    if (updatingComments) return;
    const count = await getOnchainCommentCount();
    const v2Comments = userMessages.filter(
      (comment) => comment.version === '2'
    );
    if (count > v2Comments.length) {
      setUpdatingComments(true);
      fetchAndUpdateOnchainComments();
    }
  };

  const getOnchainCommentCount = async () => {
    const count = await getCommentCount({ portal: portalAddress!, ddocId });
    return count;
  };

  const fetchAndUpdateOnchainReplies = async (comment: IComment) => {
    const updatedReplies = await fetchReplies(
      comment.id!,
      comment.commentIndex!
    );
    await updateRepliesOnCommentV2(ddocId, comment.id!, [...updatedReplies]);
  };

  const checkReplies = async (comment: IComment) => {
    const count = await getReplyCount({
      portal: portalAddress!,
      ddocId,
      commentIndex: comment.commentIndex!,
    });
    if (count > comment.replies!.length) {
      fetchAndUpdateOnchainReplies(comment);
    }
  };

  const handlePendingComments = async () => {
    const v2Comments = userMessages.filter(
      (comment) => comment.version === '2'
    );
    for (let index = 0; index < v2Comments.length; index++) {
      const comment = v2Comments[index];
      if (comment.version !== '2') return;
      if (!Object.hasOwn(comment, 'commentIndex')) {
        if (comment.txHash) {
          const isSuccessful = await checkIfCommentTxSuccessful(
            comment.txHash as Hex
          );
          if (
            isSuccessful.status &&
            !isNaN(isSuccessful.commentIndex as number)
          ) {
            updateCommentIndexV2(
              ddocId,
              comment.id!,
              isSuccessful.commentIndex as number
            );
            continue;
          }
        }
        setCommentQueue((prev) => [...prev, comment]);
      }
    }
  };

  const handlePendingReplies = async () => {
    const v2Comments = userMessages.filter(
      (comment) => comment.version === '2'
    );
    for (let index = 0; index < v2Comments.length; index++) {
      const comment = v2Comments[index];

      for (let _index = 0; _index < comment.replies!.length; _index++) {
        if (!Object.hasOwn(comment, 'commentIndex')) return;

        const reply = comment.replies![_index];
        if (!Object.hasOwn(reply, 'commentIndex')) {
          if (reply.txHash) {
            const isSuccessful = await checkIfReplyTxSuccessful(
              reply.txHash as Hex
            );
            if (
              isSuccessful.status &&
              !isNaN(isSuccessful.replyIndex as number)
            ) {
              updateReplyCommentIndexV2(
                ddocId,
                comment.id!,
                reply.id!,
                isSuccessful.replyIndex as number
              );
              continue;
            }
          }
          setReplyQueue((prev) => [
            ...prev,
            {
              reply,
              commentId: comment.id as string,
              commentIndex: comment.commentIndex!,
            },
          ]);
        }
      }
    }
  };

  useEffect(() => {
    if (!portalAddress || !commentKey) return;
    const v2Comments = userMessages.filter(
      (comment) => comment.version === '2'
    );
    v2Comments.forEach((comment) => {
      checkReplies(comment);
    });
  }, [portalAddress, commentKey]);

  useEffect(() => {
    if (!portalAddress || !commentKey) return;
    if (smartAccountClient) {
      handlePendingComments();
      handlePendingReplies();
    }
  }, [portalAddress, commentKey, smartAccountClient]);
  useMigrateComments({
    ddocId,
    commentKey,
    isOldDoc: (version && version !== LATEST_DDOC_VERSION) || false,
  });

  //  Note: Reset account state when unauthenticated
  useEffect(() => {
    if (!isConnected) {
      setCollaboratorName('');
    }
  }, [isConnected]);

  const decryptContent = async (enc: string) => {
    const { nonce, encryptedMessage } = extractNonceFromMessage(ddocId, enc);
    const decryptedChattext = await decryptData(
      encryptedMessage,
      commentKey,
      nonce
    );
    return JSON.parse(decryptedChattext);
  };

  const getYjsUpdate = (update: any) => {
    let data = [];
    try {
      data = JSON.parse(update);
    } catch (error) {
      data = [update];
    }

    return data;
  };

  const processCommentFromIndexer = async (comments: CommentsArray) => {
    if (shouldPauseProcessingRef.current) return;
    for (const comment of comments) {
      try {
        const cachedDdoc = await getCommentItemById(
          ddocId,
          comment.resolvedContent.id
        );
        const d = await decryptContent(comment.resolvedContent.encryptedData);
        if (d.highlightYjsDiff) {
          applyContent(getYjsUpdate(d.highlightYjsDiff));
        }
        if (d.resolvedUpdate) {
          applyContent(getYjsUpdate(d.resolvedUpdate));
        }
        if (d.unresolvedUpdate) {
          applyContent(getYjsUpdate(d.unresolvedUpdate));
        }
        if (d.deletedUpdate) {
          applyContent(getYjsUpdate(d.deletedUpdate));
        }
        const _replies = await populateReplies(comment.replies, decryptContent);

        if (!cachedDdoc) {
          await addCommentToCacheV2({
            ddocId,
            id: comment.resolvedContent.id!,
            username: comment.resolvedContent.username!,
            commentIndex: comment.commentIndex,
            content: d.content,
            highlightYjsDiff: d.highlightYjsDiff,
            selectedContent: d.selectedText,
            createdAt: new Date(
              comment.resolvedContent.timestamp || comment.timestamp
            ),
            lastReadTimestamp: new Date(comment.timestamp)?.getTime()!,
            reactions: [],
            replies: _replies,
            resolved: comment.isResolved,
            deleted: comment.isDeleted,
            version: '2',
            contentHash: comment.contentHash,
            txHash: '',
          });
          continue;
        }
        const previousReplies = cachedDdoc.replies || [];
        const latestReplies = removeDuplicatesRepliesByLatest([
          ..._replies,
          ...previousReplies,
        ]) as any[];

        await updateRepliesOnCommentV2(
          ddocId,
          comment.resolvedContent.id,
          latestReplies
        );
        await updateResolveOnCommentV2(
          ddocId,
          comment.resolvedContent.id,
          comment.isResolved
        );
        await updateDeleteOnCommentV2(
          ddocId,
          comment.resolvedContent.id as string,
          comment.isDeleted
        );
        await updateTimestampOnCommentV2(
          ddocId,
          comment.resolvedContent.id,
          new Date(comment.resolvedContent.timestamp || comment.timestamp)
        );
      } catch (error) {
        console.log('ERROR while processing', comment._id);
      }
    }
  };

  const handleRealTimeUpdates = async () => {
    let response: CommentsArray = [];
    try {
      response = await getCommentsDataById(ddocId);
    } catch (error) {
      console.log(error);
      captureException(error);
    }
    if (response.length === 0) return;
    await processCommentFromIndexer(response);
  };

  const intervalRef = useRef<any | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!ddocId || !portalAddress || !commentKey) return;

    handleIncomingComments();

    let isSubscribed = true;

    const startPolling = async () => {
      while (isSubscribed) {
        if (!isProcessingRef.current) {
          isProcessingRef.current = true;
          try {
            await handleRealTimeUpdates();
          } catch (error) {
            console.error('Polling error:', error);
            captureException(error);
          } finally {
            isProcessingRef.current = false;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 20000));
      }
    };

    intervalRef.current = setTimeout(startPolling, 20000);

    return () => {
      isSubscribed = false;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [ddocId, portalAddress, commentKey]);

  const handleResolveDeleteQueue = async () => {
    const selectedOperation = operationQueue[0];
    const { data } = selectedOperation;
    try {
      setProcessingOperationQueue(true);
      if (selectedOperation.job === 'resolve') {
        await handleResolveCommentTransaction(
          data.commentIndex,
          data.contentHash,
          data.update,
          data.commentId
        );
      } else if (selectedOperation.job === 'delete') {
        await handleDeleteCommentTransaction(
          data.commentIndex,
          data.contentHash,
          data.update,
          data.commentId
        );
      } else if (selectedOperation.job === 'unresolve') {
        await handleUnresolveCommentTransaction(
          data.commentIndex,
          data.contentHash,
          data.update,
          data.commentId
        );
      }

      setOperationQueue((prev) => {
        const newQueue = [...prev].filter(
          (job) => job.id !== selectedOperation.id
        );
        return newQueue;
      });
    } catch (error) {
      console.log(error);
    } finally {
      setProcessingOperationQueue(false);
    }
  };

  useEffect(() => {
    if (
      isProcessingOperation ||
      operationQueue.length === 0 ||
      !smartAccountClient ||
      !portalAddress
    )
      return;
    handleResolveDeleteQueue();
  }, [
    isProcessingOperation,
    operationQueue,
    smartAccountClient,
    portalAddress,
  ]);

  return {
    handleNewComment,
    handleReplyOnComment,
    initialComments: userMessages,
    // setInitialComment,
    commentDrawerOpen,
    setCommentDrawerOpen,
    handleHiglightUpdate,
    handleResolveComment,
    handleUnresolveComment,
    handleDeleteComment,
    isCommentConnected: isConnected,
    isAccountLoading: isLoading,
    connectViaWallet,
    connectViaUsername: createRandomAccount,
    isSyncingComments: commentQueue.length > 0 || replyQueue.length > 0,
  };
};

export default useCommentV2;

export const populateReplies = async (
  replies: Reply[],
  decryptContent: (enc: string) => Promise<any>
) => {
  const seenIds = new Set<string>();

  return Promise.all(
    replies.flatMap((reply) => {
      if (seenIds.has(reply.resolvedContent.id)) return []; // Prevents undefined in the array
      seenIds.add(reply.resolvedContent.id);

      return decryptContent(reply.resolvedContent.encryptedData).then(
        (decrypted) => ({
          id: reply._id,
          username: reply.resolvedContent.username,
          selectedContent: decrypted.selectedContent,
          content: decrypted.content,
          replies: [],
          createdAt: new Date(
            reply.resolvedContent.timestamp || reply.timestamp
          ),
          resolved: false,
          deleted: reply.isDeleted,
          commentIndex: reply.replyIndex,
          txHash: '',
        })
      );
    })
  );
};
